import { useState, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { apis } from '@modules/apiClients'
import { QueryResult } from './types'
import { AxiosError } from 'axios'
import { addQueryToHistory } from '../QueryHistory/queryHistoryStorage'
import { useSetAtom } from 'jotai'
import { queryHistoryAtom } from '../QueryHistory/historyAtoms'
import { ChallengeService } from '@duck/api-definitions'

interface QueryRunnerProps {
  challengeId: string
  onResultsChange: (result: QueryResult) => void
}

export const useQueryRunner = ({ challengeId, onResultsChange }: QueryRunnerProps) => {
  const setQueryHistory = useSetAtom(queryHistoryAtom)
  const [isRunning, setIsRunning] = useState(false)
  const queryClient = useQueryClient()

  const runQueryRequest = useCallback(
    async (query: string) =>
      await apis.challengeService.runQuery({
        challengeId,
        query,
      })
    ,
    [challengeId]
  )

  // History list is populated with 4 copies of the initial query on page load. This is a possible workaround to avoid this.
  const runQuery = useCallback(async (queryInput: string, skipHistory = false) => {
    const trimmedQuery = queryInput.trim()
    if (!trimmedQuery) return

    const queryKey = ['challenge-query', challengeId, trimmedQuery] as const

    const cachedResult = queryClient.getQueryData<QueryResult>(queryKey)
    if (cachedResult) {
      onResultsChange({
        ...cachedResult,
        isFromCache: true
      })
      if (!skipHistory && !cachedResult.error) {
        const newQueryHistory = addQueryToHistory(challengeId, trimmedQuery)
        setQueryHistory(newQueryHistory)
      }
      return
    }

    setIsRunning(true)
    try {
      const result = await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const startTime = performance.now()
          const { data } = await runQueryRequest(trimmedQuery)
          const endTime = performance.now()
          const duration = Math.round(endTime - startTime)

          if (!skipHistory && !data.error) {
            const newQueryHistory = addQueryToHistory(challengeId, trimmedQuery)
            setQueryHistory(newQueryHistory)
          }

          return {
            results: data.error ? [] : (data.results || []),
            error: data.error ? { error: data.error, hint: data.hint || '' } : null,
            timing: duration,
            isFromCache: false
          } as QueryResult
        },
        staleTime: Infinity,
        // Do not automatically retry failed queries (including timeouts)
        retry: false,
      })

      onResultsChange(result)
    } catch (error: unknown) {
      let errorMessage = 'Network error occurred while running the query'
      let errorCode: ChallengeService.ChallengeErrorCode | undefined

      if (error instanceof AxiosError && error.response?.data) {
        const responseData = error.response.data as ChallengeService.ErrorResponse
        if (responseData.error) {
          errorMessage = responseData.error
        }
        if (responseData.errorCode) {
          errorCode = responseData.errorCode
        }
      }

      const result: QueryResult = {
        results: [],
        error: { error: errorMessage, hint: '', errorCode },
        isFromCache: false
      }
      queryClient.setQueryData(queryKey, result)
      onResultsChange(result)
    } finally {
      setIsRunning(false)
    }
  }, [challengeId, onResultsChange, queryClient, runQueryRequest])

  return {
    runQuery,
    isRunning
  }
}